import React, { useRef, useEffect, useState } from "react";
import { fieldValidation as validation } from "../utils/field-validation";
import fieldPropTypes from "./field-prop-types";
import ValidationMessage from "../theme/default/validation-message";
import ErrorBox from "../components/error-box";

const RadioButtons = ({ components: { Radio, Label }, field, appData }) => {
  const {
    name,
    options,
    isErrorStatus = false,
    title = "",
    type,
    params = "",
    accessibility = {}
  } = field;
  const {
    fieldValidation,
    resetFieldValidation,
    showAsterisk,
    screenValidation,
    isUnifiedRegistration
  } = appData;
  const prevValue = appData?.values?.[name];
  const isRadioContainer = options?.length === 1;
  const tabIndex = isRadioContainer ? -1 : 0;
  const { label: textLabel } = accessibility;

  const updateValidationData = () => {
    validation[type].update(field, appData, { fieldRef });
  };
  const [currentValue, setCurrentValue] = useState(prevValue);
  const fieldRef = useRef();
  const errorRef = useRef();

  useEffect(() => {
    if (!isRadioContainer) {
      updateValidationData();
    }
  }, [screenValidation]);

  useEffect(() => {
    // this weird check is needed as MAPI returns different types for "selected" - string and boolean
    const selectedOption = options.find(
      ({ selected }) => selected === "true" || selected === true
    );

    if (selectedOption) {
      const { value } = selectedOption;

      if (!isRadioContainer) {
        appData.handleChange({ [name]: value });
      }

      setCurrentValue(value);
    }

    return () => {
      resetFieldValidation(name);
    };
  }, []);

  const handleRadioButtonChange = e => {
    const newValue = e?.target?.value;

    const selectedOption = options.find(o => o.selected === true);

    if (selectedOption) {
      selectedOption.selected = false;

      //get options id from event
      const currentOptionId = e?.target?.id.split("-")?.at(-1);

      if (currentOptionId) {
        options[currentOptionId].selected = true;
      }
    }
    if (!isRadioContainer) {
      appData.handleChange({ [name]: newValue });
      resetFieldValidation(name);
    }

    setCurrentValue(newValue);
  };

  const isChecked = ({ selected, value }) => {
    if (isRadioContainer) {
      // this weird check is needed as MAPI returns different types for "selected" - string and boolean
      return selected === "true" || selected === true;
    } else {
      return currentValue === value;
    }
  };

  const fieldValidationData = fieldValidation[name];
  const isInvalid = () => fieldValidationData && !fieldValidationData.isValid;
  const isRequired = showAsterisk && !params.includes("IsOptional") && title;

  return (
    <div
      className={`radio-group-container ${params.split("|").join(" ")}`}
      ref={fieldRef}
      role='radiogroup'
      aria-labelledby={`${name}-label`}
    >
      {appData.isUnifiedRegistration && isInvalid() && (
        <ErrorBox
          ref={errorRef}
          errorMessage={
            appData?.settings?.translations.select_option_to_continue
          }
        />
      )}
      {title && (
        <Label
          name={name}
          text={title}
          isRequired={isRequired}
          params={params}
          invalid={isInvalid()}
          appData={appData}
          srLabel={textLabel}
        />
      )}
      <div className='radio-buttons-container'>
        {options.map((option, index) => (
          <Radio
            name={option.name}
            key={option.value}
            fieldsName={name}
            checked={isChecked(option)}
            value={option?.value}
            onChange={handleRadioButtonChange}
            tabIndex={tabIndex}
            label={option.text}
            isErrorStatus={isErrorStatus || isInvalid()}
            index={index}
          />
        ))}
      </div>
      {!isUnifiedRegistration && (
        <ValidationMessage isInvalid={isInvalid()}>
          {fieldValidationData?.errorMessage}
        </ValidationMessage>
      )}
    </div>
  );
};

RadioButtons.propTypes = fieldPropTypes;

export default RadioButtons;
